// Table calculation utilities for PdfTableViewer

/**
 * Calculate column statistics (sum, max, min, unique count)
 */
export const calculateColumnStats = (tableData, colIndex) => {
  if (!tableData || !tableData.rows) {
    return { sum: "-", max: "-", min: "-", unique: "-" };
  }

  const values = tableData.rows
    .map((row) => row[colIndex])
    .filter((val) => val !== null && val !== undefined && val !== "");
  if (values.length === 0) {
    return { sum: "-", max: "-", min: "-", unique: "-" };
  }

  // Calculate unique count for all data types
  const uniqueValues = new Set(values.map((val) => String(val).trim()));
  const uniqueCount = uniqueValues.size;

  // Try to convert to numbers
  const numericValues = values
    .map((val) => {
      const num = parseFloat(String(val).replace(/[,$%]/g, ""));
      return isNaN(num) ? null : num;
    })
    .filter((val) => val !== null);

  if (numericValues.length > 0 && numericValues.length >= values.length * 0.7) {
    // Mostly numeric data
    const sum = numericValues.reduce((acc, val) => acc + val, 0);
    const max = Math.max(...numericValues);
    const min = Math.min(...numericValues);
    return {
      sum: sum.toLocaleString(),
      max: max.toLocaleString(),
      min: min.toLocaleString(),
      unique: uniqueCount.toString(),
    };
  } else {
    // String data
    return {
      sum: "-",
      max: "-",
      min: "-",
      unique: uniqueCount.toString(),
    };
  }
};

/**
 * Convert table data to CSV format
 */
export const convertTableToCSV = (tableData) => {
  if (!tableData) return "";

  const rows = [];

  // Add headers
  if (tableData.headers && tableData.headers.length > 0) {
    rows.push(tableData.headers);
  }

  // Add data rows
  if (tableData.rows && tableData.rows.length > 0) {
    rows.push(...tableData.rows);
  }

  // Convert to CSV format
  return rows
    .map((row) =>
      row
        .map((cell) => {
          // Escape cells that contain commas, quotes, or newlines
          const cellStr = String(cell || "");
          if (
            cellStr.includes(",") ||
            cellStr.includes('"') ||
            cellStr.includes("\n")
          ) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(",")
    )
    .join("\n");
};

/**
 * Generate cell differences for highlighting changes between table versions
 */
export const getCellDifferences = (
  originalData,
  newData,
  pendingGridLineChanges,
  selectedTable,
  pdfPageDimensions
) => {
  if (
    !originalData ||
    !newData ||
    !pendingGridLineChanges ||
    !originalData.headers ||
    !newData.headers ||
    !originalData.rows ||
    !newData.rows
  ) {
    return { changes: new Set(), cellMap: {}, changedColumns: new Set() };
  }

  const changes = new Set();
  const cellMap = {};
  const changedColumns = new Set();

  // For column splits, only consider the split column and the new column as changed
  const originalColumnCount = originalData.headers.length;
  const newColumnCount = newData.headers.length;
  const isColumnSplit = newColumnCount > originalColumnCount;

  if (isColumnSplit) {
    // For column splits, identify the split column by finding where content differs
    // Since we know exactly 1 new column was added, we can find the insertion point
    let splitColumnIndex = -1;

    // Identify split column by analyzing grid line coordinates and nearby words
    if (
      pendingGridLineChanges.newGridLines &&
      pendingGridLineChanges.originalGridLines
    ) {
      // Get current page from the first affected page (should be where the change happened)
      const analysisPage = pendingGridLineChanges.affectedPages?.[0] || 1;
      const newGridLines = pendingGridLineChanges.newGridLines[analysisPage];
      const originalGridLines =
        pendingGridLineChanges.originalGridLines[analysisPage];

      if (newGridLines?.vertical && originalGridLines?.vertical) {
        // Find the new grid line (should be one more than original)
        const newVerticalLines = newGridLines.vertical;
        const originalVerticalLines = originalGridLines.vertical;

        if (newVerticalLines.length > originalVerticalLines.length) {
          // Find the new line (assuming it's the last one added)
          const newLine = newVerticalLines[newVerticalLines.length - 1];
          const gridLineX = newLine.x0;

          // Find words near this grid line and determine which column they belong to
          const wordsNearGridLine = [];
          const threshold = 50; // pixels

          // Search through selectedTable to find words near the grid line
          Object.keys(selectedTable || {}).forEach((cellKey) => {
            if (cellKey.match(/^\d+_\d+$/)) {
              const cellData = selectedTable[cellKey];
              if (cellData && cellData[0] && cellData[0].words) {
                const [, colStr] = cellKey.split("_");
                const colIndex = parseInt(colStr);

                cellData[0].words.forEach((word) => {
                  const bbox = word.bbox;
                  if (!bbox) return;

                  // Calculate word X position
                  let wordX;
                  if (!pdfPageDimensions || !pdfPageDimensions.current) {
                    return; // Skip this word if pdfPageDimensions is not available
                  }
                  const { width: pdfWidth } = pdfPageDimensions.current;

                  if (bbox.left !== undefined && bbox.width !== undefined) {
                    if (bbox.left <= 1 && bbox.width <= 1) {
                      wordX = (bbox.left + bbox.width / 2) * pdfWidth;
                    } else {
                      wordX = bbox.left + bbox.width / 2;
                    }
                  } else {
                    const x0 = bbox.x0 || bbox.x || 0;
                    const x1 = bbox.x1 || bbox.right || 0;
                    if (x0 <= 1 && x1 <= 1) {
                      wordX = ((x0 + x1) / 2) * pdfWidth;
                    } else {
                      wordX = (x0 + x1) / 2;
                    }
                  }

                  // Check if word is near the grid line
                  if (Math.abs(wordX - gridLineX) < threshold) {
                    wordsNearGridLine.push({
                      cellKey,
                      colIndex,
                      wordX,
                      word: word.text,
                    });
                  }
                });
              }
            }
          });

          if (wordsNearGridLine.length > 0) {
            // Find the most common column index near the grid line
            const columnCounts = {};
            wordsNearGridLine.forEach(({ colIndex }) => {
              columnCounts[colIndex] = (columnCounts[colIndex] || 0) + 1;
            });

            const mostCommonColumn = Object.keys(columnCounts).reduce((a, b) =>
              columnCounts[a] > columnCounts[b] ? a : b
            );
            splitColumnIndex = parseInt(mostCommonColumn);
          }
        }
      }
    }

    // If we couldn't determine from coordinates, use content-based detection
    if (splitColumnIndex === -1) {
      for (let colIndex = 0; colIndex < originalColumnCount; colIndex++) {
        // Check if this column's content differs between original and new
        let hasContentDifference = false;

        for (
          let rowIndex = 0;
          rowIndex < Math.min(originalData.rows.length, newData.rows.length);
          rowIndex++
        ) {
          const originalCell = originalData.rows[rowIndex]?.[colIndex] || "";
          const newCell = newData.rows[rowIndex]?.[colIndex] || "";

          if (originalCell !== newCell) {
            hasContentDifference = true;
            break;
          }
        }

        if (hasContentDifference) {
          splitColumnIndex = colIndex;
          break;
        }
      }
    }

    // Mark the split column and new column as changed
    if (splitColumnIndex >= 0) {
      changedColumns.add(splitColumnIndex); // Original column that was split
      changedColumns.add(splitColumnIndex + 1); // New column created

      // Mark all cells in these columns as changed
      for (
        let rowIndex = 0;
        rowIndex < Math.max(originalData.rows.length, newData.rows.length);
        rowIndex++
      ) {
        const originalCell =
          originalData.rows[rowIndex]?.[splitColumnIndex] || "";
        const newCell = newData.rows[rowIndex]?.[splitColumnIndex] || "";
        const newColumnCell =
          newData.rows[rowIndex]?.[splitColumnIndex + 1] || "";

        // Original column (split column)
        const cellKey1 = `${rowIndex}_${splitColumnIndex}`;
        const isModified = originalCell !== newCell;
        if (isModified) {
          changes.add(cellKey1);
          changedColumns.add(splitColumnIndex);
        }
        cellMap[cellKey1] = {
          type: isModified ? "modified" : "unchanged",
          original: originalCell,
          new: newCell,
        };

        // New column (created from split)
        const cellKey2 = `${rowIndex}_${splitColumnIndex + 1}`;
        if (newColumnCell) {
          // Only add if there's actually content in the new column
          changes.add(cellKey2);
          changedColumns.add(splitColumnIndex + 1);
        }
        cellMap[cellKey2] = {
          type: "added",
          original: "",
          new: newColumnCell,
        };
      }
    }
  } else {
    // Regular grid line movement - compare all cells
    const maxRows = Math.max(originalData.rows.length, newData.rows.length);
    const maxCols = Math.max(
      originalData.headers.length,
      newData.headers.length
    );

    for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
      for (let colIndex = 0; colIndex < maxCols; colIndex++) {
        const originalCell = originalData.rows[rowIndex]?.[colIndex] || "";
        const newCell = newData.rows[rowIndex]?.[colIndex] || "";

        if (originalCell !== newCell) {
          const cellKey = `${rowIndex}_${colIndex}`;
          changes.add(cellKey);
          changedColumns.add(colIndex);

          // Determine change type
          let changeType = "modified";
          if (originalCell === "" && newCell !== "") {
            changeType = "added";
          } else if (originalCell !== "" && newCell === "") {
            changeType = "removed";
          }

          cellMap[cellKey] = {
            type: changeType,
            original: originalCell,
            new: newCell,
          };
        }
      }
    }
  }

  return { changes, cellMap, changedColumns };
};
